/* eslint-disable camelcase */
import React, { useEffect, useState } from 'react';
import { useMutation, useQueryClient } from 'react-query';
import { toast } from 'react-toastify';
import AlertPopup from '../../../components/alert-popup';
import AnimationWrapper from '../../../components/animation/animationWrapper';
import Button from '../../../components/button';
import Table from '../../../components/table';
import ToastComponent from '../../../components/toast';
import Heading from '../../../components/typography';
import InitialValuesForm from '../../../constants/initialValues';
import { COLORS } from '../../../constants/style/color';
import { TradingBlackoutConfigurationHeader } from '../../../constants/table-headers/tradingBlackoutConfigurationHeader';
import moment from 'moment-timezone';
import { DATE_FORMAT_WITH_TIME } from '../../../constants/common';
import {
  useAdvanceBoolean,
  useBoolean,
  useGetAllDepartments,
  useGetAllEmployeeType,
  useGetAllJobTitles,
  useGetTablePreferences,
  useGetTradingBlackoutConfigList,
  useGetTradingBlackoutConfigView,
  usePostTablePreferences,
  useWealthlaneCustomerApi,
} from '../../../hooks';
import {
  BlackoutService,
  type Wealthlane_CustomerService_Dtos_Blackout_BlackoutListDTO,
} from '@wealthlane/api-services/wealthlane-customer-service';
import { type ModalStatusType, type TableParamsType } from '../../../types/common';
import {
  SettingSystemUserButton,
  SettingSystemUserHeader,
  SystemUserContainer,
} from '../system-users/systemUserStyled';
import TradingBlackoutConfigurationModel from './tradingBlackoutConfigurationModel';
import {
  useDynamicTable,
  DynamicTable,
  withDynamicTableContext,
  useTableSchemaLoader,
} from '@wealthlane/table-builder';
import Spinners from '../../../components/spinner';
import { axiosInstance } from '../../../services/apiInterceptor';

const TradingBlackoutConfiguration = (): JSX.Element => {
  const { handleRequest } = useWealthlaneCustomerApi();
  const queryClient = useQueryClient();
  const { tableInfo, setTableInfo } = useDynamicTable();
  const [headerList, setHeaderList] = useState<string[]>([]);

  useEffect(() => {
    setTableInfo(InitialValuesForm.tableParams);
  }, []);

  // const [tableInfo, setTableInfo] = useState<TableParamsType>(InitialValuesForm.tableParams);
  const [modalStatus, setModalStatus] = useState<ModalStatusType>('Add');
  const [initialBlackoutConfig, setInitialBlackoutConfig] = useState(
    InitialValuesForm.tradingBlackoutConfig
  );
  const { value: isModalOpen, setFalse: closeModal, setTrue: openModal } = useBoolean(false);
  const {
    value: isConfirmOpen,
    setFalse: closeConfirmModal,
    setTrue: openConfirmModal,
    setCustomValue,
    customValue,
  } = useAdvanceBoolean(false);

  const { data, status } = useGetTradingBlackoutConfigList(handleRequest, tableInfo);
  const estNormalizedData = React.useMemo(() => {
    if (data?.items == null) return [];

    return data.items.map((item: any) => ({
      ...item,
      startDate: item.startDate
        ? moment(item.startDate).tz('America/New_York').format(`${DATE_FORMAT_WITH_TIME}`)
        : item.startDate,
      endDate: item.endDate
        ? moment(item.endDate).tz('America/New_York').format(`${DATE_FORMAT_WITH_TIME}`)
        : item.endDate,
      lastModifiedDate: item.lastModifiedDate
        ? moment(item.lastModifiedDate).tz('America/New_York').format(`${DATE_FORMAT_WITH_TIME}`)
        : item.lastModifiedDate,
      creationTime: item.creationTime
        ? moment(item.creationTime).tz('America/New_York').format(`${DATE_FORMAT_WITH_TIME}`)
        : item.creationTime,
    }));
  }, [data?.items]);
  const { data: departmentOptions } = useGetAllDepartments(handleRequest);
  const { data: employeeTypeOptions } = useGetAllEmployeeType(handleRequest);
  const { data: jobTitleOptions } = useGetAllJobTitles(handleRequest);
  const { mutate: getBlackoutConfigView, isLoading: viewLoader } = useGetTradingBlackoutConfigView(
    handleRequest,
    setInitialBlackoutConfig
  );

  const { data: tablePreferences } = useGetTablePreferences(
    handleRequest,
    'trading blackout config'
  );

  useEffect(() => {
    tablePreferences != null && setHeaderList([...tablePreferences]);
  }, [tablePreferences]);

  const { mutate: postTablePreference, isLoading: tablePrefLoading } =
    usePostTablePreferences(handleRequest);

  const handleSavePreference = (value: string[]): void => {
    const prefPayload = {
      tableName: 'trading blackout config',
      preferences: value,
    };
    postTablePreference(prefPayload);
  };

  const { schemaData, isTableSchemaLoading } = useTableSchemaLoader({
    identifier: 'TRADING_BLACKOUT_CONFIG_TABLE',
    baseUrl: process.env.REACT_APP_WEALTHLANE_API_ENDPOINT,
  });

  const tableActions = [
    {
      action: 'View',
      as: 'Create',
      onClick: (id: string, rowData: Wealthlane_CustomerService_Dtos_Blackout_BlackoutListDTO) => {
        setModalStatus('View');
        rowData.blackoutId != null &&
          rowData.blackoutId !== '' &&
          getBlackoutConfigView({ id: rowData.blackoutId, modalStatus: 'Add' });
        openModal();
      },
      id: 'viewTradingBlackoutConfigAction',
    },
    {
      action: 'Edit',
      as: 'Edit',
      onClick: (id: string, rowData: Wealthlane_CustomerService_Dtos_Blackout_BlackoutListDTO) => {
        setModalStatus('Edit');
        rowData.blackoutId != null &&
          rowData.blackoutId !== '' &&
          getBlackoutConfigView({ id: rowData.blackoutId, modalStatus: 'Edit' });
        openModal();
      },
      id: 'editTradingBlackoutConfigAction',
    },
    {
      action: 'Repurpose',
      as: 'Create',
      onClick: (id: string, rowData: Wealthlane_CustomerService_Dtos_Blackout_BlackoutListDTO) => {
        setModalStatus('Add');
        rowData.blackoutId != null &&
          rowData.blackoutId !== '' &&
          getBlackoutConfigView({ id: rowData.blackoutId, modalStatus: 'Repurpose' });
        openModal();
      },
      id: 'repurposeTradingBlackoutConfigAction',
    },
    {
      action: <div className='delete-label'>Delete</div>,
      as: 'Delete',
      onClick: async (id: string, rowData: any) => {
        setCustomValue(rowData?.blackoutId);
        setModalStatus('Delete');
        openConfirmModal();
      },
      id: 'deleteTradingBlackoutConfigAction',
    },
  ];
  const useDeleteTradingBlackoutConfig = (): any => {
    return useMutation(
      async () => {
        return await handleRequest(
          BlackoutService.deleteApiCustomerApiAppBlackoutBlackout(customValue)
        );
      },
      {
        onSuccess: (response: any) => {
          if (response != null) {
            closeConfirmModal();
            setCustomValue('');
            toast.success(
              <ToastComponent
                label={'Success'}
                message={'Trading blackout configuration deleted.'}
              />
            );
            void queryClient.invalidateQueries('getTradingBlackoutConfigList');
          }
        },
        onError: (err) => {
          console.log('error', err);
        },
      }
    );
  };
  const { mutate, isLoading } = useDeleteTradingBlackoutConfig();

  const tableHeaderList = TradingBlackoutConfigurationHeader({
    actions: schemaData?.columns,
    tableActions,
  })
    .filter((item) => item.isSubHeader)
    .map((item) => {
      // console.log('item', item);

      return {
        label: item.Header,
        value: item.accessor,
      };
    });

  const tradingblackoutHeader = (): any[] => {
    return TradingBlackoutConfigurationHeader({
      actions: schemaData?.columns,
      tableActions,
    }).filter((item) => item.isSubHeader === false || headerList.includes(item.accessor));
  };

  const schema = React.useMemo(
    () => ({
      columns: tradingblackoutHeader(),
      options: schemaData?.options as any,
    }),
    [schemaData]
  );
  const renderRightToolbarButton = () => {
    return (
      <Button
        // as='Create'
        variant={'primary'}
        title={'Add Blackout'}
        icon={'add_circle_outline'}
        id="addTradingBlackoutConfigButton"
        onClick={() => {
          setModalStatus('Add');
          openModal();
        }}
      />
    );
  };
  if (isTableSchemaLoading) return <Spinners containerClassName='h-screen' />;

  return (
    <SystemUserContainer data-page="tradingBlackoutConfiguration" id="pageContainer">
      <div className='flex justify-between items-center' id="pageHeader">
        <SettingSystemUserHeader id="headerContent">
          <Heading variant='h3' title='Trading Blackout Configuration' id="pageTitle" />
          <Heading
            variant='body-s'
            title='Secure your account & manage your password.'
            color={`${COLORS.Gray}`}
            id="pageDescription"
          />
        </SettingSystemUserHeader>
      </div>

      <AnimationWrapper>
        {isModalOpen && (
          <TradingBlackoutConfigurationModel
            id="tradingBlackoutConfigModal"
            isModalOpen={isModalOpen}
            closeModal={() => {
              closeModal();
              setInitialBlackoutConfig({
                ...InitialValuesForm.tradingBlackoutConfig,
                blackoutGroups: [
                  {
                    id: null,
                    groupData: false,
                    customerDepartmentId: null,
                    employeeTypeId: null,
                    blackOutJobTitles: [],
                  },
                ],
              });
            }}
            modalStatus={modalStatus}
            loading={viewLoader}
            setModalStatus={setModalStatus}
            initialBlackoutConfig={initialBlackoutConfig}
            departmentOptions={departmentOptions}
            employeeTypeOptions={employeeTypeOptions}
            jobTitleOptions={jobTitleOptions}
          />
        )}
      </AnimationWrapper>
      <DynamicTable
        id="tradingBlackoutConfigTable"
        schema={schema}
        data={estNormalizedData}
        totalCounts={data?.totalCount ?? 10}
        loading={status}
        components={{
          renderRightToolbarButton,
        }}
        tableColumnList={tableHeaderList}
        selectedColumnList={headerList}
        tablePreferenceLoading={tablePrefLoading}
        handleColumnSave={handleSavePreference}
      />
      {/* <Table
        columns={TradingBlackoutConfigurationHeader(actions)}
        data={data?.items ?? []}
        loading={status}
        showPagination
        totalCounts={data?.totalCount ?? 10}
        tableInfo={tableInfo}
        setTableInfo={setTableInfo}
      /> */}
      {isConfirmOpen && (
        <AlertPopup
          id="deleteTradingBlackoutConfigConfirmModal"
          isShow={isConfirmOpen}
          handleSubmit={() => mutate(customValue)}
          closeModal={closeConfirmModal}
          modalStatus={modalStatus}
          title={'this trading blackout configuration'}
          loading={isLoading}
        />
      )}
    </SystemUserContainer>
  );
};

export default withDynamicTableContext(TradingBlackoutConfiguration, false, axiosInstance);
